import { c as _c } from "react/compiler-runtime";
import { IcoCopy16, IcoPencil16, IcoTrash16 } from '@onefootprint/icons';
import { IconButton, Tooltip, useConfirmationDialog } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import { DocumentPane } from '../../section-editor/document-pane';
import TitleEditor from '../../section-editor/title-editor';
import { useBuilder } from '../../store/provider';
import type { ManagedNode } from '../../store/types';
import { ProsePreview } from '../shared/inline-edit';
import FieldRow from '../task/field-row';
import ValidationAlerts from '../validation-alerts';
import InstructionsCallout from './instructions-callout';
import SectionRenderer from './section-renderer';
import ToolAvailabilityRow from './tool-availability-row';
type ManagedBlockProps = {
  node: ManagedNode;
  /** 1-based position in the plan, matching the number the nav shows for this node. */
  position: number;
};

// One generic block for every managed kind — identity/prompt/controls come from the server-owned
// `template`, so a new template is a backend-only add.
const ManagedBlock = t0 => {
  const $ = _c(54);
  const {
    node,
    position
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.managed"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    actions,
    isReadOnly
  } = useBuilder();
  const confirm = useConfirmationDialog();
  let t2;
  if ($[1] !== actions || $[2] !== confirm || $[3] !== node.id || $[4] !== t) {
    t2 = () => {
      confirm.open({
        title: t(_temp),
        description: t(_temp2),
        variant: "destructive",
        primaryButton: {
          label: t(_temp3),
          onClick: () => actions.makeManagedNodeCustom(node.id)
        },
        secondaryButton: {
          label: t(_temp4)
        }
      });
    };
    $[1] = actions;
    $[2] = confirm;
    $[3] = node.id;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleMakeCustom = t2;
  let t3;
  if ($[6] !== actions || $[7] !== confirm || $[8] !== node.id || $[9] !== node.template.title || $[10] !== t) {
    t3 = () => {
      confirm.open({
        title: t(_temp5),
        description: t(_temp6, {
          name: node.template.title
        }),
        variant: "destructive",
        primaryButton: {
          label: t(_temp7),
          onClick: () => actions.removeNode(node.id)
        },
        secondaryButton: {
          label: t(_temp8)
        }
      });
    };
    $[6] = actions;
    $[7] = confirm;
    $[8] = node.id;
    $[9] = node.template.title;
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleDelete = t3;
  const t4 = `managed-${node.id}`;
  let t5;
  if ($[12] !== position || $[13] !== t) {
    t5 = t(_temp9, {
      position
    });
    $[12] = position;
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = node.template.title;
  const t7 = node.template.title;
  let t8;
  if ($[15] !== t) {
    t8 = t(_temp10);
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== node.template.title || $[18] !== t8) {
    t9 = <TitleEditor value={t6} placeholder={t7} ariaLabel={t8} editable={false} onChange={_temp11} />;
    $[17] = node.template.title;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== actions || $[21] !== handleDelete || $[22] !== handleMakeCustom || $[23] !== isReadOnly || $[24] !== node || $[25] !== t) {
    t10 = !isReadOnly && <><Tooltip content={t(_temp12)}><IconButton aria-label={t(_temp13)} icon={IcoCopy16} size="compact" variant="ghost" onClick={() => actions.copyNode(node)} /></Tooltip><Tooltip content={t(_temp14)}><IconButton aria-label={t(_temp15)} icon={IcoPencil16} size="compact" variant="ghost" onClick={handleMakeCustom} /></Tooltip><Tooltip content={t(_temp16)}><IconButton aria-label={t(_temp17)} icon={IcoTrash16} size="compact" variant="ghost" onClick={handleDelete} /></Tooltip></>;
    $[20] = actions;
    $[21] = handleDelete;
    $[22] = handleMakeCustom;
    $[23] = isReadOnly;
    $[24] = node;
    $[25] = t;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== node.id) {
    t11 = <ValidationAlerts nodeId={node.id} />;
    $[27] = node.id;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = node.template.prompt;
  let t13;
  if ($[29] !== t) {
    t13 = t(_temp18);
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== node.template.prompt || $[32] !== t13) {
    t14 = <ProsePreview value={t12} placeholder={t13} canEdit={false} size="large" />;
    $[31] = node.template.prompt;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== node.template.instructions) {
    t15 = <InstructionsCallout instructions={node.template.instructions} />;
    $[34] = node.template.instructions;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== node.template.requiredTools) {
    t16 = <ToolAvailabilityRow tools={node.template.requiredTools} />;
    $[36] = node.template.requiredTools;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== node) {
    t17 = node.template.sections.map((section, index) => <SectionRenderer key={section.title ?? `section-${index}`} node={node} section={section} />);
    $[38] = node;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== node.task.fields || $[41] !== node.task.id) {
    t18 = node.task.fields.length > 0 && <div className="flex flex-col gap-2">{node.task.fields.map(field => <FieldRow key={field.id} readOnly={true} taskId={node.task.id} field={field} />)}</div>;
    $[40] = node.task.fields;
    $[41] = node.task.id;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t10 || $[44] !== t11 || $[45] !== t14 || $[46] !== t15 || $[47] !== t16 || $[48] !== t17 || $[49] !== t18 || $[50] !== t4 || $[51] !== t5 || $[52] !== t9) {
    t19 = <DocumentPane id={t4} eyebrow={t5} title={t9} actions={t10}>{t11}{t14}{t15}{t16}{t17}{t18}</DocumentPane>;
    $[43] = t10;
    $[44] = t11;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
    $[50] = t4;
    $[51] = t5;
    $[52] = t9;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  return t19;
};
export default ManagedBlock;
function _temp($) {
  return $.makeCustom.title;
}
function _temp2($_0) {
  return $_0.makeCustom.description;
}
function _temp3($_1) {
  return $_1.makeCustom.confirm;
}
function _temp4($_2) {
  return $_2.cancel;
}
function _temp5($_3) {
  return $_3.delete.title;
}
function _temp6($_4) {
  return $_4.delete.description;
}
function _temp7($_5) {
  return $_5.delete.confirm;
}
function _temp8($_6) {
  return $_6.cancel;
}
function _temp9($_7) {
  return $_7.eyebrow;
}
function _temp10($_8) {
  return $_8.name.label;
}
function _temp11() {}
function _temp12($_9) {
  return $_9.copy.ariaLabel;
}
function _temp13($_10) {
  return $_10.copy.ariaLabel;
}
function _temp14($_11) {
  return $_11.makeCustom.label;
}
function _temp15($_12) {
  return $_12.makeCustom.label;
}
function _temp16($_13) {
  return $_13.delete.ariaLabel;
}
function _temp17($_14) {
  return $_14.delete.ariaLabel;
}
function _temp18($_15) {
  return $_15.prompt.placeholder;
}